import { Color, Entity, Quat, Vec3 } from 'playcanvas';

import { Element, ElementType } from './element';
import { Serializer } from './serializer';

/**
 * Directional light used for shadow-only lighting / shadow catcher meshes.
 *
 * Note: This is intentionally minimal and editor-focused (single directional light).
 */
class ShadowLight extends Element {
    entity: Entity;

    private _name = 'Shadow Light';

    enabled = true;
    intensity = 1;
    color = new Color(1, 1, 1);

    // PlayCanvas light component shadow settings (best-effort defaults)
    castShadows = true;
    shadowDistance = 75;
    shadowResolution = 2048;

    constructor() {
        super(ElementType.shadow);

        this.entity = new Entity('ShadowLight');
        // Default: aim light "down" so a horizontal ground plane doesn't get lambert falloff.
        // Directional light direction is derived from entity rotation.
        this.entity.setLocalEulerAngles(90, 0, 0);
        this.entity.setLocalPosition(0, 0, 0);
    }

    set name(v: string) {
        this._name = v;
        try {
            this.entity.name = v;
        } catch {
            // ignore
        }
        this.scene?.events?.fire('shadow.name', this);
    }

    get name() {
        return this._name;
    }

    add() {
        if (!this.scene) {
            throw new Error('ShadowLight.add() called but scene is null');
        }

        // Attach under contentRoot so it is cleared with scene content.
        this.scene.contentRoot.addChild(this.entity);

        // Create directional light.
        this.entity.addComponent('light', {
            type: 'directional',
            color: this.color,
            intensity: this.intensity,
            castShadows: this.castShadows,
            shadowDistance: this.shadowDistance,
            shadowResolution: this.shadowResolution
        } as any);

        this.applySettingsToComponent();
    }

    applySettingsToComponent() {
        try {
            const light: any = (this.entity as any).light;
            if (light) {
                if (light.color && typeof light.color.copy === 'function') {
                    light.color.copy(this.color);
                } else {
                    light.color = this.color;
                }
                if (typeof light.intensity === 'number') {
                    light.intensity = this.intensity;
                } else {
                    light.intensity = this.intensity;
                }
                light.castShadows = this.castShadows;
                light.shadowDistance = this.shadowDistance;
                light.shadowResolution = this.shadowResolution;
            }
            this.entity.enabled = this.enabled;
        } catch {
            // ignore
        }
    }

    remove() {
        try {
            this.entity.destroy();
        } catch {
            // ignore
        }
    }

    serialize(serializer: Serializer) {
        serializer.packa(this.entity.getWorldTransform().data);
        serializer.pack(this.enabled);
        serializer.pack(this.intensity);
        serializer.pack(this.color.r, this.color.g, this.color.b);
        serializer.pack(this.castShadows);
        serializer.pack(this.shadowDistance);
        serializer.pack(this.shadowResolution);
    }

    move(position?: Vec3, rotation?: Quat, scale?: Vec3) {
        if (position) this.entity.setLocalPosition(position);
        if (rotation) this.entity.setLocalRotation(rotation);
        if (scale) this.entity.setLocalScale(scale);
    }
}

export { ShadowLight };
